import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from "@angular/router";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { debounceTime, finalize, Observable, of, Subject, takeUntil, tap, throwError } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { SelectItem } from "primeng/api";

// Subjects / Services
import { SegmentSubjectService } from "../../../subject/segment.subject";
import { PropertySubject } from "../../../subject/property.subject";
import { PropertyInitialSubject } from "../../../subject/property.initial.subject";
import { MapsSubject } from "../../../subject/maps.subject";

import { CatalogService } from "../../../services/catalog.service";
import { CityService } from "../../../services/city.service";
import { ZoneService } from "../../../services/zone.service";
import { PropertyService } from "../../../services/property.service";
import { FlowModeSubject } from "../../../subject/flow.mode.subject";
import { FlowService } from "../../../services/flow.service";
import { FlowStateService } from "../../../services/flow.state.service";

// DTOs / Enums
import { SegmentInformationDto } from "../../../dto/segment.information.dto";
import { PropertyAddressDto } from "../../../dto/property.address.dto";
import { PropertyInitialResponseDto } from "../../../dto/property.initial.response.dto";
import { ResponseDto } from "../../../dto/response.dto";
import { FlowMode } from "../../../dto/enum/flow.mode.enum";
import { RecordStageEnum } from "../../../dto/enum/record.stage.enum";

type PersistenceOp = 'CREATE' | 'UPDATE';
const STEP_KEY = 'address';

@Component({
    selector: 'app-property-address',
    templateUrl: './property-address.component.html',
    styleUrls: ['./property-address.component.scss']
})
export class PropertyAddressComponent implements OnInit, OnDestroy {

    // --------- Modo / flujo -------------
    mode: FlowMode = FlowMode.CREATE;
    isEditMode = false;
    persistenceOp: PersistenceOp = 'CREATE';

    // --------- Navegación -------------
    nextUrl = '';
    previousUrl = '';

    // --------- Estado general -------------
    loading = false;
    private destroy$ = new Subject<void>();

    // --------- UI / formulario -------------
    addressForm!: FormGroup;
    isNotDataValid = false;
    isFocused: { [key: string]: boolean } = {};

    // --------- Catálogos -------------
    departments: SelectItem[] = [];
    city: SelectItem[] = [];
    neighborhood: SelectItem[] = [];

    selectedDepartment: string | null = null;
    selectedCity: string | null = null;
    selectedNeighborhood: string | null = null;
    loadCityList = true;
    loadNeighborhoodList = true;

    // --------- Datos auxiliares -------------
    property: PropertyInitialResponseDto | null = null; // del subject
    propertySegment: SegmentInformationDto | null = null;
    propertyName: string | undefined = '';
    propertyNickname: string | null = null;

    propertyMapsDto: PropertyAddressDto = {
        cityCode: '',
        cityName: '',
        departmentCode: '',
        departmentName: '',
        zoneCode: '',
        zoneName: ''
    };

    // --------- Errores -------------
    dialogMessage = '';
    dialogCode?: string;
    showErrorDialog = false;

    constructor(
        private router: Router,
        private formBuilder: FormBuilder,

        // subjects
        private segmentSubject: SegmentSubjectService,
        private propertySubject: PropertySubject,
        private propertyInitialSubject: PropertyInitialSubject,
        private mapSubject: MapsSubject,

        // servicios
        private catalogService: CatalogService,
        private cityService: CityService,
        private zoneService: ZoneService,
        private propertyService: PropertyService,

        // flujo
        private flowModeSubject: FlowModeSubject,
        private flowService: FlowService,
        private flowState: FlowStateService
    ) {}

    // -----------------------------------------------------------
    // Ciclo de vida
    // -----------------------------------------------------------
    ngOnInit(): void {
        // 1) Modo y rutas
        const mode = this.flowModeSubject.getCurrentMode();
        if (!mode) {
            this.router.navigate(['/list-pending']);
            return;
        }
        this.mode = mode;
        this.isEditMode = mode === FlowMode.EDIT;

        this.previousUrl = this.flowService.getPreviousStep(STEP_KEY);
        this.nextUrl = this.flowService.getNextStep(STEP_KEY);

        // 2) Paso actual en estado global
        this.flowState.setCurrentStep(STEP_KEY);

        // 3) Inicializar form + autosave
        this.initForm();
        this.bindAutosave();

        // 4) Obtener propiedad inicial (si hay)
        this.property = this.propertyInitialSubject.getCurrentProperty() ?? null;
        if (this.property?.recordStage) {
            // persistimos recordStage y propertyId en el estado global para tener fuente única
            this.flowState.setRecordStage(this.property.recordStage as RecordStageEnum);
            if (this.property.propertyId) this.flowState.setPropertyId(this.property.propertyId);
        }

        // 5) Decidir operación de persistencia (CREATE/UPDATE)
        this.persistenceOp = this.decidePersistenceOp();

        // 6) Bootstrap según contexto
        if (this.isEditMode) {
            // EDIT → siempre hay backend
            const id = this.property?.propertyId || this.flowState.propertyId;
            if (!id) { this.router.navigate(['/list-pending']); return; }
            this.bootstrapFromBackend(id);
            return;
        }

        if (this.mode === FlowMode.RESUME) {
            const id = this.property?.propertyId || this.flowState.propertyId;
            if (id) {
                // En RESUME puede existir o no data del paso en backend.
                // Intentamos traer; si no hay, solo cargamos catálogos y seguimos.
                this.bootstrapFromBackend(id, /*soft*/ true);
                return;
            }
        }

        // CREATE puro o no hay nada en backend → intentar cache local
        const cached = this.flowState.getStepData(STEP_KEY);
        if (cached) {
            this.restoreFromCache(cached);
            return;
        }

        // Cargar datos base desde subjects (segmento y nickname)
        this.propertySegment = this.segmentSubject.getSelectedSegment();
        this.propertyNickname = this.propertySubject.getPropertyName();
        this.propertyName = this.propertySegment?.segmentLabel ?? '';
        if (!this.propertySegment?.segmentId) {
            this.router.navigate(['/segments']);
            return;
        }

        this.loadDepartments$().pipe(takeUntil(this.destroy$)).subscribe();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // -----------------------------------------------------------
    // Política de persistencia (CREATE vs UPDATE)
    // -----------------------------------------------------------
    private decidePersistenceOp(): PersistenceOp {
        // EDIT → siempre UPDATE
        if (this.mode === FlowMode.EDIT) return 'UPDATE';

        // Si ya hay propertyId, en general has iniciado algo → UPDATE
        if (this.flowState.propertyId || this.property?.propertyId) {
            // En RESUME, manda la comparación de pasos
            if (this.mode === FlowMode.RESUME) {
                const stage = (this.property?.recordStage as RecordStageEnum) || this.flowState.recordStage;
                if (!stage) return 'UPDATE'; // sin stage, asumimos update
                const stageStep = this.flowState.getStepByRecordStage(stage);
                const currentIdx = this.flowService.getStepIndex(STEP_KEY);
                const stageIdx = this.flowService.getStepIndex(stageStep);
                if (stageIdx === -1 || currentIdx === -1) return 'UPDATE'; // seguro
                // Regla del usuario:
                // - si estás en el mismo paso o uno anterior → UPDATE
                // - si estás en un paso posterior → CREATE
                return (currentIdx <= stageIdx) ? 'UPDATE' : 'CREATE';
            }
            return 'UPDATE';
        }

        // CREATE inicial (no hay propertyId)
        return 'CREATE';
    }

    // -----------------------------------------------------------
    // Bootstrap desde backend
    // -----------------------------------------------------------
    private bootstrapFromBackend(propertyId: number, soft = false): void {
        this.loading = true;
        this.propertyService.getPropertyAddress(propertyId).pipe(
            takeUntil(this.destroy$),
            switchMap((p) => {
                if (!p) throw new Error('No hay datos en address para esta propiedad');
                // cache base
                this.propertySubject.setPropertyResponse(p);
                this.propertyNickname = p.propertyName;
                this.propertySegment = {
                    segmentId: p.segmentId,
                    segmentCode: p.segmentCode,
                    segmentLabel: p.segmentLabel,
                    description: '',
                    fileUrl: ''
                };
                return this.loadDepartments$().pipe(
                    switchMap(() => this.loadCities$(p.ctDepartment)),
                    switchMap(() => this.loadNeighborhoods$(p.cityId)),
                    tap(() => this.patchFormWithProperty(p)),
                    tap(() => this.persistToFlowState())
                );
            }),
            finalize(() => (this.loading = false)),
            catchError(err => {
                // En modo "soft" (RESUME), si 404 o no hay data, solo carga catálogos.
                if (soft) {
                    this.loadDepartments$().pipe(takeUntil(this.destroy$)).subscribe();
                    return of(null);
                }
                this.responseHandler(err);
                return throwError(() => err);
            })
        ).subscribe();
    }

    // -----------------------------------------------------------
    // Reactive Form
    // -----------------------------------------------------------
    private initForm(): void {
        this.addressForm = this.formBuilder.group({
            department: ['', Validators.required],
            city: [{ value: '', disabled: true }, Validators.required],
            neighborhood: [{ value: '', disabled: true }, Validators.required],
            propertyNumber: ['', Validators.required],
            propertyAddress: ['', Validators.required],
            externalNumber: ['', [Validators.required, Validators.pattern(/^[0-9]+$/)]],
            internalNumber: [''],
            locationReference: ['', Validators.required],
            catastralCode: ['', Validators.required]
        });
    }

    private patchFormWithProperty(p: any): void {
        this.selectedDepartment = p.ctDepartment ?? null;
        this.selectedCity = p.cityId ?? null;

        this.addressForm.get('department')?.setValue(this.selectedDepartment);
        if (this.selectedDepartment) this.addressForm.get('city')?.enable();
        this.addressForm.get('city')?.setValue(this.selectedCity);

        const matchedZone = this.neighborhood.find(n => (n.label ?? '').toLowerCase() === (p.zoneName ?? '').toLowerCase());
        this.selectedNeighborhood = matchedZone?.value ?? null;
        if (this.selectedNeighborhood) {
            this.addressForm.get('neighborhood')?.enable();
            this.addressForm.get('neighborhood')?.setValue(this.selectedNeighborhood);
        }

        this.addressForm.patchValue({
            propertyNumber: p.propertyNumber,
            propertyAddress: p.propertyAddress,
            externalNumber: p.externalNumber,
            internalNumber: p.internalNumber,
            locationReference: p.locationReference,
            catastralCode: p.catastralCode
        });
    }

    private bindAutosave(): void {
        this.addressForm?.valueChanges
            .pipe(debounceTime(300), takeUntil(this.destroy$))
            .subscribe(() => this.persistToFlowState());
    }

    private persistToFlowState(): void {
        this.flowState.setStepData(STEP_KEY, {
            formValues: this.addressForm.getRawValue(), // incluir controles deshabilitados
            selectedDepartment: this.selectedDepartment,
            selectedCity: this.selectedCity,
            selectedNeighborhood: this.selectedNeighborhood,
            propertySegment: this.propertySegment,
            propertyName: this.propertyName,
            propertyNickname: this.propertyNickname
        });
    }

    private restoreFromCache(cached: any): void {
        this.propertySegment = cached.propertySegment ?? null;
        this.propertyName = cached.propertyName ?? '';
        this.propertyNickname = cached.propertyNickname ?? null;

        this.selectedDepartment = cached.selectedDepartment ?? null;
        this.selectedCity = cached.selectedCity ?? null;
        this.selectedNeighborhood = cached.selectedNeighborhood ?? null;

        this.loadDepartments$()
            .pipe(
                switchMap(() => this.selectedDepartment ? this.loadCities$(this.selectedDepartment) : of([])),
                switchMap(() => this.selectedCity ? this.loadNeighborhoods$(this.selectedCity) : of([])),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.addressForm.patchValue(cached.formValues ?? {});
                if (this.selectedDepartment) this.addressForm.get('city')?.enable();
                if (this.selectedCity) this.addressForm.get('neighborhood')?.enable();
            });
    }

    // -----------------------------------------------------------
    // Catálogos
    // -----------------------------------------------------------
    private loadDepartments$(): Observable<SelectItem[]> {
        return this.catalogService.getCatalogList('CT_DEPARTMENT').pipe(
            map((options: any[]) => (options ?? []).map(o => ({ label: o.optionName, value: o.optionCode }))),
            tap((list) => (this.departments = list)),
            catchError(error => { console.error('Error al cargar departamentos', error); return of([]); })
        );
    }

    private loadCities$(departmentCode: string): Observable<SelectItem[]> {
        return this.cityService.getDepartmentCityList(departmentCode).pipe(
            map((options: any[]) => (options ?? []).map(o => ({ label: o.cityName, value: o.cityId }))),
            tap(list => { this.city = list; this.addressForm.get('city')?.enable(); }),
            catchError(error => { console.error('Error al cargar ciudades', error); return of([]); })
        );
    }

    private loadNeighborhoods$(cityCode: string): Observable<SelectItem[]> {
        return this.zoneService.getZoneList(cityCode).pipe(
            map((options: any[]) => (options ?? []).map(o => ({ label: o.zoneName, value: o.zoneId }))),
            tap(list => { this.neighborhood = list; this.addressForm.get('neighborhood')?.enable(); }),
            catchError(error => { console.error('Error al cargar barrios', error); return of([]); })
        );
    }

    // -----------------------------------------------------------
    // UI events
    // -----------------------------------------------------------
    onDepartmentChange(event: any): void {
        this.selectedDepartment = event.value;
        this.addressForm.get('city')?.reset();
        this.addressForm.get('neighborhood')?.reset();
        this.addressForm.get('city')?.disable();
        this.addressForm.get('neighborhood')?.disable();
        if (!this.selectedDepartment) return;

        this.loadCities$(this.selectedDepartment).pipe(takeUntil(this.destroy$))
            .subscribe(() => this.persistToFlowState());
    }

    onCityChange(event: any): void {
        this.selectedCity = event.value;
        this.addressForm.get('neighborhood')?.reset();
        this.addressForm.get('neighborhood')?.disable();
        if (!this.selectedCity) return;

        this.loadNeighborhoods$(this.selectedCity).pipe(takeUntil(this.destroy$))
            .subscribe(() => this.persistToFlowState());
    }

    selectNeighborhoods(event: any): void {
        this.selectedNeighborhood = event.value;
        this.propertyMapsDto = {
            cityCode: this.selectedCity ?? '',
            cityName: this.city.find(c => c.value === this.selectedCity)?.label || '',
            departmentCode: this.selectedDepartment ?? '',
            departmentName: this.departments.find(d => d.value === this.selectedDepartment)?.label || '',
            zoneCode: this.selectedNeighborhood ?? '',
            zoneName: this.neighborhood.find(n => n.value === this.selectedNeighborhood)?.label || ''
        };
        this.mapSubject.saveMap(this.propertyMapsDto);
        this.persistToFlowState();
    }

    handleFocus(field: string): void { this.isFocused[field] = true; }
    handleBlur(field: string): void { this.isFocused[field] = false; }

    // -----------------------------------------------------------
    // Guardar (CREATE / UPDATE) según política
    // -----------------------------------------------------------
    handleNextStep(): void {
        this.saveProperty().subscribe({
            next: () => this.router.navigate([this.nextUrl]),
            error: () => {} // already handled
        });
    }

    handleSaveAndExit(): void {
        this.saveProperty().subscribe({
            next: () => this.router.navigate(['/list-pending']), // o dashboard
            error: () => {}
        });
    }

    handlePreviousStep(): void {
        this.router.navigate([this.previousUrl]);
    }

    saveProperty(): Observable<any> {
        if (this.addressForm.invalid) {
            this.isNotDataValid = true;
            this.addressForm.markAllAsTouched();
            return throwError(() => new Error('Formulario inválido'));
        }

        // recalcular por si recordStage cambió en esta sesión
        this.persistenceOp = this.decidePersistenceOp();

        const payload = this.buildPayload();
        this.persistToFlowState();

        if (this.persistenceOp === 'UPDATE') {
            const id = this.property?.propertyId || this.flowState.propertyId;
            if (!id) return throwError(() => new Error('No hay propertyId para UPDATE'));
            return this.doUpdate(id, payload);
        }

        return this.doCreate(payload);
    }

    private doCreate(propertyData: any): Observable<any> {
        this.loading = true;
        return this.propertyService.createProperty(propertyData).pipe(
            tap((res) => {
                const newId = res?.propertyId ?? res?.id;
                if (newId) {
                    this.flowState.setPropertyId(newId);
                    // al guardar address, el stage pasa a ADDRESS (o el que tu backend defina)
                    this.flowState.setRecordStage(RecordStageEnum.ADDRESS);
                    // mantener compatibilidad con el subject inicial
                    this.propertyInitialSubject.updateProperty({
                        propertyId: newId,
                        propertyStatus: 'PENDING_PROP',
                        recordStage: RecordStageEnum.ADDRESS,
                        segmentId: this.propertySegment?.segmentId,
                        segmentCode: this.propertySegment?.segmentCode,
                        segmentLabel: this.propertySegment?.segmentLabel,
                        segmentIconPath: this.propertySegment?.fileUrl
                    } as any);
                }
            }),
            finalize(() => (this.loading = false)),
            catchError((error) => {
                this.responseHandler(error);
                return throwError(() => new Error('Error al registrar la propiedad'));
            })
        );
    }

    private doUpdate(propertyId: number, propertyData: any): Observable<any> {
        this.loading = true;
        return this.propertyService.updateProperty(propertyId, propertyData).pipe(
            tap(() => {
                this.flowState.setPropertyId(propertyId);
                this.flowState.setRecordStage(RecordStageEnum.ADDRESS);
            }),
            finalize(() => (this.loading = false)),
            catchError((error) => {
                this.responseHandler(error);
                return throwError(() => new Error('Error al actualizar la propiedad'));
            })
        );
    }

    private buildPayload(): any {
        const raw = this.addressForm.getRawValue(); // incluye disabled
        return {
            segmentId: this.propertySegment?.segmentId,
            propertyName: this.propertyNickname,
            propertyNumber: raw.propertyNumber,
            propertyAddress: raw.propertyAddress,
            externalNumber: raw.externalNumber,
            internalNumber: raw.internalNumber,
            locationReference: raw.locationReference,
            catastralCode: raw.catastralCode,
            accountOwner: true,
            zoneId: this.selectedNeighborhood,
            ctUseStatus: (this.persistenceOp === 'UPDATE') ? 'UPDATE_PROPERTY' : 'NEW_PROPERTY'
        };
    }

    // -----------------------------------------------------------
    // Errores / validaciones
    // -----------------------------------------------------------
    responseHandler(responseDto: ResponseDto | any) {
        const error = responseDto?.errorDetail || responseDto?.message || 'Error inesperado';
        const status = responseDto?.statusCode || responseDto?.status || '500';
        this.dialogDisplayError(error, status);
    }

    dialogDisplayError(error: string | null, status: string) {
        this.showErrorDialog = true;
        this.dialogMessage = error ?? '';
        this.dialogCode = status;
    }

    getValidationMessages(state: any, thingName?: string) {
        const thing = thingName;
        const messages: string[] = [];
        if (state.errors != null) {
            for (const errorName of Object.keys(state.errors)) {
                switch (errorName) {
                    case 'required': messages.push(`El campo ${thing} es requerido.`); break;
                    case 'minlength': messages.push(`El campo ${thing} debe ser al menos ${state.errors.minlength.requiredLength} caracteres.`); break;
                    case 'maxlength': messages.push(`El campo ${thing} no debe ser mayor a ${state.errors.maxLength.requiredLength} caracteres.`); break;
                    case 'pattern': messages.push(`El campo ${thing} contiene caracteres inválidos.`); break;
                    case 'email': messages.push(`El campo ${thing} debe ser correo electrónico.`); break;
                    case 'min': messages.push(`El importe debe ser 1.00 como mínimo.`); break;
                }
            }
        }
        return messages;
    }
}
